/**
 * Product Status Constants - Story 6.2: Create My Products Page
 * Centralized configuration for product status values, labels, and display options
 * Prevents hardcoding of status strings across the application
 */

import React from 'react';
import { ProductStatus } from '../types/products.types';
import { CheckCircle2, AlertCircle } from 'lucide-react';

// Re-export ProductStatus for convenience
export { ProductStatus } from '../types/products.types';

/**
 * Status Display Labels
 * Maps enum values to user-friendly display labels
 */
export const STATUS_LABELS: Record<ProductStatus, string> = {
  [ProductStatus.ACTIVE]: 'Active',
  [ProductStatus.INACTIVE]: 'Inactive',
};

/**
 * Status Display Label with ALL option
 * Extends STATUS_LABELS to include the "ALL" filter option
 */
export const STATUS_LABELS_WITH_ALL = {
  ALL: 'All Status',
  [ProductStatus.ACTIVE]: 'Active',
  [ProductStatus.INACTIVE]: 'Inactive',
} as const;

/**
 * Status Display Configuration
 * Complete styling and configuration for each status
 */
export interface StatusConfig {
  label: string;
  variant: 'default' | 'destructive' | 'secondary' | 'outline';
  className: string;
  icon: React.ReactNode;
  bgColor: string;
  textColor: string;
}

export const STATUS_CONFIG: Record<ProductStatus, StatusConfig> = {
  [ProductStatus.ACTIVE]: {
    label: STATUS_LABELS[ProductStatus.ACTIVE],
    variant: 'default',
    className: 'bg-green-100 text-green-800 border-green-300',
    icon: React.createElement(CheckCircle2, { className: 'w-4 h-4' }),
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
  },
  [ProductStatus.INACTIVE]: {
    label: STATUS_LABELS[ProductStatus.INACTIVE],
    variant: 'destructive',
    className: 'bg-gray-100 text-gray-700 border-gray-300',
    icon: React.createElement(AlertCircle, { className: 'w-4 h-4' }),
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-600',
  },
};

/**
 * Status Filter Options
 * Options for the status filter dropdown in MyProductsPage
 */
export const STATUS_FILTER_OPTIONS = [
  { value: 'ALL', label: STATUS_LABELS_WITH_ALL.ALL },
  { value: ProductStatus.ACTIVE, label: STATUS_LABELS_WITH_ALL[ProductStatus.ACTIVE] },
  { value: ProductStatus.INACTIVE, label: STATUS_LABELS_WITH_ALL[ProductStatus.INACTIVE] },
] as const;

/**
 * Get display label for a status value
 * @param status - Product status
 * @returns User-friendly label
 */
export function getStatusLabel(status: ProductStatus | 'ALL'): string {
  if (status === 'ALL') {
    return STATUS_LABELS_WITH_ALL.ALL;
  }
  return STATUS_LABELS[status];
}

/**
 * Get status configuration for styling and display
 * @param status - Product status
 * @returns Status configuration object
 */
export function getStatusConfig(status: ProductStatus): StatusConfig {
  return STATUS_CONFIG[status];
}

/**
 * Get opposite status (for toggling)
 * @param status - Current product status
 * @returns Opposite status (ACTIVE -> INACTIVE or vice versa)
 */
export function getOppositeStatus(status: ProductStatus): ProductStatus {
  return status === ProductStatus.ACTIVE ? ProductStatus.INACTIVE : ProductStatus.ACTIVE;
}

/**
 * Check if status is active
 * @param status - Product status
 * @returns True if status is ACTIVE
 */
export function isActiveStatus(status: ProductStatus | undefined): boolean {
  return status === ProductStatus.ACTIVE;
}

/**
 * Check if status is inactive
 * @param status - Product status
 * @returns True if status is INACTIVE
 */
export function isInactiveStatus(status: ProductStatus | undefined): boolean {
  return status === ProductStatus.INACTIVE;
}
